import React, { ChangeEvent, useMemo } from 'react';
import styled from 'styled-components';

import Modal from '@atlaskit/modal-dialog';
import Row from 'components/Common/Row';
import Typography from 'kingpin/atoms/Typography';
import CloseButton from 'components/CloseButton';
import Button from 'kingpin/atoms/Button';
import Loading from 'components/Loading/Loading';
import PreviousUploadItem from '../PreviousUploadItem';
import { TopBarDiv } from 'screens/Boards/PerformanceBoardForm';
import UploadSection from './UploadSection';
import WarningSvg from '../icons/warning.svg';
import SuccessSvg from '../icons/success.svg';
import ErrorSection from './ErrorSection';

const Content = styled.div<{ isFullScreen: boolean }>`
  padding: ${(props) => (props.isFullScreen ? '16px 24px' : '32px 40px')};
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
  overflow-y: ${(props) => (props.isFullScreen ? 'auto' : 'hidden')};
`;

const UploadServiceFailureModal = ({
  isFullScreen,
  close,
  dataType,
  file,
  setFile,
  onFileUpload,
  isUploading,
  inputFile,
  onFileChange,
  isLoading,
  sortedPreviousUploads,
  errors,
  errorMessage,
  configError,
  successMessage,
}: {
  isFullScreen: boolean;
  close?: () => void;
  dataType: DataManager.UploadDataType;
  file: File | undefined;
  setFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  onFileUpload: () => void;
  isUploading: boolean;
  inputFile: React.MutableRefObject<HTMLInputElement | null>;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  sortedPreviousUploads: DataManager.PreviousUpload[];
  errors: string[];
  errorMessage?: string;
  successMessage?: string;
  configError: string | undefined;
}) => {
  const MODAL_WIDTH = '520px';
  const Body = useMemo(() => {
    return (
      <>
        {successMessage && (
          <Row
            data-testid={'success-upload'}
            style={{
              backgroundColor: 'var(--color-success-v2)',
              padding: '16px',
              position: 'absolute',
              width: isFullScreen ? '100%' : MODAL_WIDTH,
              top: 0,
            }}
            centerAlign
          >
            <img
              src={SuccessSvg}
              alt="success"
              style={{ marginRight: '8px' }}
            />
            <Typography.Body type="Label" color={'var(--color-text-inverse)'}>
              {successMessage}
            </Typography.Body>
          </Row>
        )}

        {configError && (
          <Row
            data-testid={'failure-upload'}
            style={{
              backgroundColor: 'var(--color-status-error)',
              padding: '16px',
              position: 'absolute',
              width: MODAL_WIDTH,
              top: 0,
            }}
            centerAlign
          >
            <img src={WarningSvg} alt="error" style={{ marginRight: '8px' }} />
            <Typography.Body
              type="Label"
              color={'var(--color-text-inverse)'}
            >{`Config error: ${configError}`}</Typography.Body>
          </Row>
        )}
        <Content isFullScreen={isFullScreen}>
          <div>
            <TopBarDiv>
              <Row
                centerAlign
                spaceBetween
                style={{ height: '100%', paddingBottom: 16 }}
              >
                <Row centerAlign style={{ height: '100%' }}>
                  <Typography.Header type="H3">
                    {`Upload ${dataType.name}`}
                  </Typography.Header>
                </Row>
                {!!close && <CloseButton close={close} />}
              </Row>
            </TopBarDiv>

            <ErrorSection errors={errors} errorMessage={errorMessage} />

            <UploadSection
              file={file}
              setFile={setFile}
              onFileUpload={onFileUpload}
              isUploading={isUploading}
              inputFile={inputFile}
              onFileChange={onFileChange}
              fileType={dataType.fileType}
              dataType={dataType.dataType}
            />

            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">
                {`Previous Uploads (${sortedPreviousUploads.length})`}
              </Typography.Body>
            </div>

            <>
              {isLoading && <Loading />}
              {!isLoading && (
                <>
                  {sortedPreviousUploads.length === 0 && (
                    <Typography.Body type="Body 14">
                      No Previous Uploads
                    </Typography.Body>
                  )}
                  {sortedPreviousUploads.length !== 0 && (
                    <div
                      style={{
                        border: `1px solid var(--color-grey-8)`,
                        borderRadius: '4px',
                      }}
                    >
                      <div
                        style={{
                          maxHeight: isFullScreen ? undefined : '144px',
                          overflowY: 'scroll',
                        }}
                      >
                        {sortedPreviousUploads.map((previousUpload) => (
                          <PreviousUploadItem
                            isFullScreen={isFullScreen}
                            key={previousUpload.id}
                            previousUpload={previousUpload}
                            isLast={
                              sortedPreviousUploads[
                                sortedPreviousUploads.length - 1
                              ].id === previousUpload.id
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          </div>
          {!!close && (
            <Row style={{ justifyContent: 'flex-end' }}>
              <Button
                size={'Small'}
                type={'Tertiary'}
                label="Close"
                onClick={close}
              />
            </Row>
          )}
        </Content>
      </>
    );
  }, [
    close,
    configError,
    dataType.dataType,
    dataType.fileType,
    dataType.name,
    errorMessage,
    errors,
    file,
    inputFile,
    isFullScreen,
    isLoading,
    isUploading,
    onFileChange,
    onFileUpload,
    setFile,
    sortedPreviousUploads,
    successMessage,
  ]);

  if (isFullScreen) {
    return <>{Body}</>;
  }

  return (
    <Modal
      width={MODAL_WIDTH}
      height={errors.length === 0 ? '632px' : '760px'}
      onClose={close}
      autoFocus={false}
    >
      {Body}
    </Modal>
  );
};

export default UploadServiceFailureModal;
